import React from "react";
import { Box } from "@mui/material";
import { tokens, displayFont } from "../theme";

/**
 * The PayLoom mark and lockups, per the brand identity book.
 *
 * Direction A is an interlace that literalizes the loom: three marigold warp
 * threads crossed by three plum weft threads — attendance, leave, timesheets
 * and payroll woven into one structure. Drawn as SVG rather than shipped as a
 * PNG so it stays sharp, recolours for dark grounds, and never gets cropped.
 *
 * Geometry is the book's 56px artboard, kept as the viewBox so every size is
 * the same drawing scaled: 9-thick threads at 7.25 / 23.5 / 39.75, radius 4.
 *
 * Rules carried over from the book:
 *   - Clear space on all sides equals the height of the mark.
 *   - Minimum digital width: 96px for the lockup, 24px for the mark alone.
 *   - Never crop, rotate, or recolour the thread bars independently.
 *   - Never place the full-colour mark on photography or a busy background —
 *     use variant="reversed" there.
 *   - variant="reversed" is for a deep plum ground (the sidebar, the payslip
 *     email header). It is the book's single-colour paper mark: blush
 *     throughout, weft at 60% opacity. No marigold.
 */

const THREADS = [7.25, 23.5, 39.75];
const THREAD = 9;
const BOX = 56;

/** The woven mark on its own. `size` is the rendered square, in px. */
export const PayLoomMark = ({ size = 32, variant = "color", title, ...rest }) => {
  const reversed = variant === "reversed";

  // Full colour is marigold warp crossed by plum weft, per the book's primary
  // lockup. Reversed is NOT that with a lighter weft — the book calls it a
  // "single-color paper mark", one blush thread colour throughout, with the
  // weave carried by opacity rather than hue: warp at full strength, weft at
  // 60%. Rendering it marigold-on-white put two colours on a dark ground where
  // the book asks for one, which is the gold-and-white grid that looked wrong.
  //
  // Note `tokens.marigold`, not `tokens.accent`: `accent` is this codebase's
  // name for the interactive colour and resolves to plum, which would render
  // the mark as plum on plum with no weave at all.
  const warp = reversed ? tokens.surface : tokens.marigold;
  const weft = reversed ? tokens.surface : tokens.primary;
  const weftOpacity = reversed ? 0.6 : 1;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${BOX} ${BOX}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title && <title>{title}</title>}
      {THREADS.map((x) => (
        <rect key={`warp-${x}`} x={x} y={0} width={THREAD} height={BOX} rx={4} fill={warp} />
      ))}
      {THREADS.map((y) => (
        <rect
          key={`weft-${y}`}
          x={0}
          y={y}
          width={BOX}
          height={THREAD}
          rx={4}
          fill={weft}
          fillOpacity={weftOpacity}
        />
      ))}
    </svg>
  );
};

/**
 * Direction B: the wordmark alone, with the marigold thread run under "Loom".
 * For tight spaces, invoice footers, co-branding and partner lockups, where a
 * symbol is one element too many.
 */
export const PayLoomWordmark = ({ size = 28, variant = "color", ...rest }) => {
  const ink = variant === "reversed" ? tokens.surface : tokens.text;
  return (
    <Box
      component="span"
      {...rest}
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        fontFamily: displayFont,
        fontWeight: 600,
        fontSize: `${size}px`,
        letterSpacing: "-0.02em",
        lineHeight: 1,
        color: ink,
        ...rest.sx,
      }}
    >
      <span>Pay</span>
      <Box component="span" sx={{ position: "relative", ml: "4px" }}>
        Loom
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: `${Math.max(2, Math.round(size * 0.14))}px`,
            height: `${Math.max(2, Math.round(size * 0.09))}px`,
            background: tokens.marigold,
            borderRadius: "6px",
            zIndex: -1,
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * The primary lockup: mark plus wordmark. The default for the app header,
 * sign-in and anywhere the brand stands alone.
 */
const PayLoomLogo = ({ size = 32, variant = "color", showMark = true, ...rest }) => {
  const ink = variant === "reversed" ? tokens.surface : tokens.text;
  return (
    <Box
      {...rest}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: `${Math.round(size * 0.29)}px`,
        ...rest.sx,
      }}
      aria-label="PayLoom"
      role="img"
    >
      {showMark && <PayLoomMark size={size} variant={variant} />}
      <Box
        component="span"
        sx={{
          fontFamily: displayFont,
          fontWeight: 600,
          fontSize: `${Math.round(size * 0.71)}px`,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: ink,
        }}
      >
        PayLoom
      </Box>
    </Box>
  );
};

export default PayLoomLogo;
